import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { keycloakInstance } from '../keycloak.config';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private refreshingToken = false;
  private refreshInterval: any;
  
  // Signal for reactive components
  public token = signal<string | null>(null);

  constructor() {
    // Initialize with any existing token
    this.updateFromKeycloak();

    // Set up a listener for token changes from Keycloak
    keycloakInstance.onTokenExpired = () => this.refreshToken();
    keycloakInstance.onAuthSuccess = () => this.updateFromKeycloak();
    keycloakInstance.onAuthLogout = () => this.clearToken();
    
    // Set up hourly token refresh
    this.setupRefreshInterval();
  }

  /**
   * Set up periodic token refresh (every hour)
   */
  private setupRefreshInterval(): void {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    
    // Set up hourly refresh (3600000 ms = 1 hour)
    this.refreshInterval = setInterval(() => {
      if (this.isAuthenticated()) {
        this.refreshToken();
      }
    }, 3600000);
  }

  /**
   * Get the current auth token
   */
  getToken(): string | null {
    return this.tokenSubject.getValue();
  }

  /**
   * Observable to track token changes
   */
  getTokenObservable(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  /**
   * Update service with latest token from Keycloak
   */
  updateFromKeycloak(): void {
    if (keycloakInstance.token) {
      this.setToken(keycloakInstance.token);
    }
  }

  /**
   * Set a new token value
   */
  setToken(token: string): void {
    this.tokenSubject.next(token);
    this.token.set(token);
  }

  /**
   * Clear the token (logout)
   */
  clearToken(): void {
    this.tokenSubject.next(null);
    this.token.set(null);
  }

  /**
   * Refresh the token
   */
  refreshToken(): Promise<boolean> {
    if (this.refreshingToken) {
      return Promise.resolve(false);
    }

    this.refreshingToken = true;
    
    return new Promise<boolean>((resolve) => {
      keycloakInstance.updateToken(30)
        .then((refreshed: any) => {
          if (refreshed && keycloakInstance.token) {
            this.setToken(keycloakInstance.token);
            resolve(true);
          } else {
            resolve(false);
          }
          this.refreshingToken = false;
        })
        .catch((error: any) => {
          // Minimal error logging
          console.error('Token refresh failed');
          this.refreshingToken = false;
          keycloakInstance.login();
          resolve(false);
        });
    });
  }

  /**
   * Check if user is authenticated (has a valid token)
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }
  
  /**
   * Clean up on service destruction
   */
  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
}